import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Skeleton,
} from "@mui/material";
import { CustomButton, HeadingText } from "../../utils/CustomTagComponents";
import { getBlogByCountry } from "../../services/BlogService";
import { useSelector } from "react-redux";

const BlogSection = () => {
  const { country_id } = useSelector((state) => state.auth || {});
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const navigate = useNavigate();

  const fetchBlogs = async (pageNo = 1, append = false) => {
    try {
      setLoading(true);
      const { data } = await getBlogByCountry(pageNo, 3, country_id);

      if (data?.success) {
        const newBlogs = data.data || [];

        setBlogs(prev => append ? [...prev, ...newBlogs] : newBlogs);

        if (newBlogs.length < 3) {
          setHasMore(false); // no more data
        }
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBlogs(1, false);
  }, [country_id]);

  if (!loading && blogs.length === 0) return null;

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      <Container sx={{
        py: { xs: 3, sm: 4, md: 6 },
        px: { xs: 3, sm: 4, md: 6 },
        paddingLeft: { xs: "24px !important", md: "48px !important" },
        paddingRight: { xs: "24px !important", md: "48px !important" }
      }}>
        <HeadingText variant="h5" mb={3}>
          Trending Insights & Blogs
        </HeadingText>

        <Grid container spacing={3}>
          {loading && page === 1
            ? Array.from({ length: 3 }).map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                <Skeleton
                  variant="rectangular"
                  height={230}
                  sx={{ borderRadius: "20px" }}
                />
                <Skeleton variant="text" sx={{ fontSize: "2rem", mt: 2 }} />
                <Skeleton variant="rounded" width={120} height={40} />
              </Grid>
            ))
            : blogs.map((blog, index) => {
              const truncatedDesc = blog.desc
                ? blog.desc.replace(/<[^>]+>/g, "").slice(0, 100) + (blog.desc.length > 200 ? "..." : "")
                : "A comprehensive guide on leveraging hybrid cloud infrastructure to reduce operational costs.";
              return (
                <Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
                  <Card
                    sx={{
                      cursor: "pointer",
                      borderRadius: "16px",
                      overflow: "hidden",
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      background: "#fff",
                      border: "1px solid #eee",
                      transition: "all 0.25s ease",
                      boxShadow: "0 8px 20px rgba(0,0,0,0.06)",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 16px 30px rgba(0,0,0,0.12)",
                      },
                    }}
                  >
                    {/* Image */}
                    <Box sx={{ position: "relative" }}>
                      <CardMedia
                        component="img"
                        image={blog.blog_image}
                        alt={blog.seo_desc}
                        sx={{ height: 230, objectFit: "cover" }}
                      />

                      {/* Category Badge */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 12,
                          left: 12,
                          bgcolor: "#ff6b00",
                          color: "#fff",
                          fontSize: 12,
                          fontWeight: 600,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: "6px",
                          textTransform: "uppercase",
                        }}
                      >
                        {blog.seo_desc}
                      </Box>
                    </Box>

                    {/* Content */}
                    <CardContent
                      sx={{
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        flexGrow: 1,
                      }}
                    >

                      {/* Title */}
                      <Typography
                        sx={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: "#0a2540",
                          lineHeight: "26px",
                        }}
                        dangerouslySetInnerHTML={{ __html: blog.title }}
                      />

                      {/* Description */}
                      <Typography
                        sx={{
                          fontSize: 14,
                          color: "#6b7280",
                          lineHeight: "22px",
                          mt: 1,
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                          minHeight: "46px", // keeps equal height
                        }}
                      >
                        {truncatedDesc}
                      </Typography>

                      {/* Read More */}
                      <Typography
                        onClick={() => navigate(`/blog/${blog.id}`)}
                        sx={{
                          mt: "auto",
                          fontSize: 14,
                          fontWeight: 600,
                          color: "#ff6b00",
                          display: "flex",
                          alignItems: "center",
                          gap: 0.5,
                          cursor: "pointer",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            color: "#e65c00",
                            transform: "translateX(3px)"
                          }
                        }}
                      >
                        Read More →
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )
            })}
        </Grid>

        {/* Load More Button */}
        {hasMore && !loading && (
          <Box sx={{ textAlign: "center", mt: 5 }}>
            <CustomButton
              variant="outlined"
              onClick={() => {
                const nextPage = page + 1;
                setPage(nextPage);
                fetchBlogs(nextPage, true);
              }}
            >
              Load More
            </CustomButton>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BlogSection;
